import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add, AddSquare, ShoppingBag } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { Link } from "@/components/Atoms";
import { FilterChip, PageHeader, SearchField, SefanoButton } from "@/components/Molecules";
import { OrderStatsRow, OrdersTable, SefanoPagination } from "@/components/Organisms";
import { PageTemplate } from "@/components/Templates/Page";
import type { GetOrdersStatus } from "@/api/schemas/getOrdersStatus";
import { GetOrdersStatus as OrderStatusEnum } from "@/api/schemas/getOrdersStatus";
import type { OrdersTemplateProps } from "./Orders.template.types";
import * as styles from "./Orders.template.styles";

export type { OrdersTemplateProps } from "./Orders.template.types";

const STATUS_FILTERS: Array<{ key: GetOrdersStatus | "all"; labelKey: string }> = [
  { key: "all", labelKey: "filters.all" },
  { key: OrderStatusEnum.created, labelKey: "filters.created" },
  { key: OrderStatusEnum.confirmed, labelKey: "filters.confirmed" },
  { key: OrderStatusEnum.preparing, labelKey: "filters.preparing" },
  { key: OrderStatusEnum.shipped, labelKey: "filters.shipped" },
  { key: OrderStatusEnum.completed, labelKey: "filters.completed" },
];

export function OrdersTemplate({
  orders,
  meta,
  stats,
  page,
  setPage,
  search,
  handleSearchChange,
  statusFilter,
  setStatusFilter,
  isLoading,
  isStatsLoading,
  isEmpty,
  paginationLabel,
  countLabel,
  isOrderCreateBlocked,
}: OrdersTemplateProps) {
  const { t } = useTranslation("orders");
  const theme = useTheme();

  const filterChips = STATUS_FILTERS.map(({ key, labelKey }) => (
    <FilterChip
      key={key}
      label={t(labelKey)}
      active={key === "all" ? statusFilter === undefined : statusFilter === key}
      onClick={() => setStatusFilter(key === "all" ? undefined : (key as GetOrdersStatus))}
    />
  ));

  const paginationSection =
    meta && meta.last_page > 1 ? (
      <Box sx={styles.paginationFooter}>
        <Typography sx={styles.paginationCount}>{paginationLabel}</Typography>
        <SefanoPagination count={meta.last_page} page={page} onChange={(_, value) => setPage(value)} />
      </Box>
    ) : meta ? (
      <Typography sx={styles.paginationCount}>{paginationLabel}</Typography>
    ) : null;

  return (
    <PageTemplate maxWidth={1440}>
      <Box sx={styles.pageStack}>
        <Box sx={styles.desktopHeader}>
          <PageHeader
            title={t("title")}
            subtitle={t("subtitle")}
            action={
              isOrderCreateBlocked ? null : (
                <Link to="/orders/create">
                  <SefanoButton
                    intent="add"
                    startIcon={<Add size={18} variant="Linear" color={theme.palette.primary.contrastText} />}
                  >
                    {t("createOrder")}
                  </SefanoButton>
                </Link>
              )
            }
          />
        </Box>

        <Box sx={styles.mobileHeader}>
          <Box sx={styles.mobileHeaderText}>
            <Typography component="h1" sx={styles.mobileTitle}>
              {t("title")}
            </Typography>
            <Typography component="p" sx={styles.mobileCountLabel}>
              {countLabel}
            </Typography>
          </Box>
          {isOrderCreateBlocked ? null : (
            <Link to="/orders/create">
              <SefanoButton
                intent="add"
                size="small"
                startIcon={<AddSquare size={16} variant="Linear" color={theme.palette.primary.contrastText} />}
                sx={styles.mobileCreateButton}
              >
                {t("mobileCreateOrder")}
              </SefanoButton>
            </Link>
          )}
        </Box>

        {!isEmpty || isLoading ? (
          <>
            <OrderStatsRow stats={stats} isLoading={isStatsLoading} />

            <Box sx={styles.desktopContentCard}>
              <Box sx={styles.toolbar}>
                <Box sx={styles.toolbarFilters}>{filterChips}</Box>
                <Box sx={styles.desktopSearchWrap}>
                  <SearchField
                    placeholder={t("searchPlaceholder")}
                    value={search}
                    onChange={handleSearchChange}
                    fieldSize="compact"
                  />
                </Box>
              </Box>

              <OrdersTable orders={orders} isLoading={isLoading} layout="desktop" />
              {paginationSection}
            </Box>

            <Box sx={styles.mobileStack}>
              <SearchField
                placeholder={t("mobileSearchPlaceholder")}
                value={search}
                onChange={handleSearchChange}
                fieldSize="compact"
              />

              <Box sx={styles.mobileFiltersRow}>{filterChips}</Box>

              <OrdersTable orders={orders} isLoading={isLoading} layout="mobile" />
              {paginationSection}
            </Box>
          </>
        ) : (
          <>
            <Box sx={styles.desktopEmptyStateCard}>
              <Box sx={styles.emptyStateIconWrap}>
                <ShoppingBag size={60} variant="Bold" color={theme.palette.primary.main} />
              </Box>
              <Typography variant="h6" fontWeight={700}>
                {t("emptyTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={styles.emptyDescription}>
                {t("emptyDescription")}
              </Typography>
              {isOrderCreateBlocked ? null : (
                <Box sx={styles.emptyActionWrap}>
                  <Link to="/orders/create">
                    <SefanoButton intent="add">{t("emptyAction")}</SefanoButton>
                  </Link>
                </Box>
              )}
            </Box>

            <Box sx={styles.mobileEmptyState}>
              <Box sx={styles.mobileEmptyIconWrap}>
                <ShoppingBag size={52} variant="Bold" color={theme.palette.primary.main} />
              </Box>
              <Typography sx={styles.mobileEmptyTitle}>{t("emptyTitle")}</Typography>
              <Typography sx={styles.mobileEmptyDescription}>{t("mobileEmptyDescription")}</Typography>
              {isOrderCreateBlocked ? null : (
                <Link to="/orders/create">
                  <SefanoButton
                    intent="add"
                    startIcon={<AddSquare size={19} variant="Linear" color={theme.palette.primary.contrastText} />}
                    sx={styles.mobileEmptyButton}
                  >
                    {t("emptyAction")}
                  </SefanoButton>
                </Link>
              )}
            </Box>
          </>
        )}
      </Box>
    </PageTemplate>
  );
}
